import React, { useState, useContext } from "react";

import { Input, notification } from "antd";
import {
  UserOutlined,
  RobotOutlined,
  DeleteOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { webAPIs } from "../config/webAPIs";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { AuthContext } from "../AuthContext";
import axiosInstance from "../config/axiosInstance";
import TokenUsageIndicator from "../components/TokenUsageIndicator";

const { TextArea } = Input;

const Dashboard = () => {
  const {
    message,
    handleResetMessage,
    files,
    setMessage,
    adminPrompt,
    selectedDocIds,
    checked,
    setLoading,
  } = useContext(AuthContext);
  const [question, setQuestion] = useState("");
  const [lastTokenInfo, setLastTokenInfo] = useState(null);

  // Get selectedProjectId from localStorage or context
  const selectedProjectId = localStorage.getItem('selectedProjectId');

  const handleKeyUp = async (e) => {
    if (e.keyCode === 13 && e.shiftKey === false) {
      await sendRequest();
    }
  };

  const sendRequest = async () => {
    window.scrollTo(0, 99999999);
    setLoading(true);
    if (question.trim().length > 0) {
      if (files && files.length > 0) {
        if (!selectedDocIds || selectedDocIds.length === 0) {
          notification.warning({
            description: "Please select at least one document",
            duration: 2,
          });
          setLoading(false);
          return;
        }
        if (!selectedProjectId) {
          notification.warning({
            description: "Please select a project",
            duration: 2,
          });
          setLoading(false);
          return;
        }
        try {
          setMessage([
            ...message,
            {
              role: "user",
              content: question,
            },
          ]);
          setQuestion("");
          const response = await axiosInstance.post(
            webAPIs.chat,
            {
              prompt: question,
              systemPrompt: adminPrompt,
              checked: checked,
              temp: 0,
              documentIds: selectedDocIds,
              projectId: selectedProjectId,
            },
            {
              timeout: 1000000,
            }
          );
          setMessage([
            ...message,
            {
              role: "user",
              content: question,
            },
            {
              role: "assistant",
              content: response.data.result,
            },
          ]);
          
          // Store token information for the last response
          if (response.data.tokenInfo) {
            setLastTokenInfo(response.data.tokenInfo);
          }
        } catch (error) {
          notification.error({
            description: error.response.data.message || "Server Error",
            duration: 2,
          });
        }
      } else {
        notification.warning({
          description: "Please upload documents",
          duration: 2,
        });
      }
    }
    setLoading(false);
  };

  const handleClearConversation = async (data = []) => {
    await axiosInstance.post(webAPIs.clearConversation, {
      conversation: data,
    });
    handleResetMessage();
  };

  const handleRemoveConversation = async (index) => {
    let temp_conversation = message;
    temp_conversation.splice(index, 2);
    handleClearConversation(temp_conversation);
  };

  return (
    <div className="w-full bg-mainColor flex flex-col">
      <div className="flex-1 h-[100%] text-textColor flex flex-col items-center text-lg overflow-y-auto">
        {message.map((item, index) => (
          <div
            key={index}
            className={`w-[100%] flex justify-center py-4 ${item.role === "assistant" ? "bg-botColor" : "bg-userColor"
              }`}
          >
            <div className="sm:w-[60%] w-full px-2 flex items-center">
              {item.role === "user" ? (
                <div className="flex items-center w-full">
                  <UserOutlined className="text-2xl mr-6" />
                  <ReactMarkdown
                    className="markdown-body flex-1 pt-3 text-textColor"
                    remarkPlugins={[remarkMath, remarkGfm]}
                    rehypePlugins={[rehypeKatex]}
                  >
                    {item.content}
                  </ReactMarkdown>
                  <DeleteOutlined
                    onClick={() => handleRemoveConversation(index)}
                  />
                </div>
              ) : (
                <>
                  <RobotOutlined className="text-2xl mr-6 text-textColor" />
                  <ReactMarkdown
                    className="markdown-body flex-1 pt-3 text-textColor"
                    remarkPlugins={[remarkMath, remarkGfm]}
                    rehypePlugins={[rehypeKatex]}
                  >
                    {item.content}
                  </ReactMarkdown>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Token Usage Indicator */}
      {lastTokenInfo && (
        <div className="px-4 py-2 bg-inputColor border-t border-t-sidebarColor">
          <TokenUsageIndicator tokenInfo={lastTokenInfo} />
        </div>
      )}
      
      <div className="pb-3 pt-3 bottom-7 w-full justify-center flex bg-inputColor border-t border-t-sidebarColor">
        <div className="relative shadow-md w-[70%] bg-textInputColor text-textColor">
          <TextArea
            className="w-full pr-12"
            rows={2}
            onKeyUp={handleKeyUp}
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value);
            }}
            placeholder="Please enter your prompt here"
          />
          <div
            className="cursor-pointer absolute right-0 bottom-1/2 transform translate-y-1/2 mr-3 h-8 w-8 bg-sidebarColor rounded-full flex items-center justify-center"
            onClick={sendRequest}
          >
            <SendOutlined className="text-white" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
